import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetArtefactScope } from '@modules/common/hooks';
import { mapToEdgeTimeRange } from '@modules/common/utils';
import { getEqlWhereString } from '@modules/dashboard/helpers';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { useCreateDashboardFromTemplateMutation } from 'generated/graphql';
import { useEffect } from 'react';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';
import { createCachedDashboardListItem } from '../helpers/createCachedDashboardListItem';

interface UseCreateDashboardFromTemplateOptions {
  templateID: string;
}

export const useCreateDashboardFromTemplate = ({
  templateID,
}: UseCreateDashboardFromTemplateOptions) => {
  const user = useGetUser();
  const query = useDashboardStore(({ query }) => query);

  const { eql } = useGetEQLExpression({
    tenantID: user.tenant.id,
    eqlProps: {
      whereClause: query.whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const artefactScope = useGetArtefactScope();

  const eqlWhereString = getEqlWhereString(eql);

  const [createDashboard, { loading, error }] =
    useCreateDashboardFromTemplateMutation({
      variables: {
        request: {
          tenantID: user.tenant.id,
          userID: user.id,
          templateID,
          scope: artefactScope,
          filterClause: {
            timeRange: query.duration
              ? mapToEdgeTimeRange(query.duration)
              : undefined,
            whereClause: eqlWhereString,
          },
        },
      },
      update: (cache, result) =>
        result.data?.createDashboardFromTemplate?.dashboard &&
        createCachedDashboardListItem(
          cache,
          result.data.createDashboardFromTemplate.dashboard,
          user,
        ),
    });

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: 'Failed to use template',
        props: {},
      });
    }
  }, [error, showErrorNotification]);

  return {
    createDashboard,
    loading,
  };
};
